import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HeroSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [popularCats, setPopularCats] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchPopular = async () => {
      try {
        const res = await axios.get(`${API}/services/popular`);
        // Pick a diverse set of 6 popular services
        const diverse = [];
        const seen = new Set();
        for (const svc of res.data) {
          if (!seen.has(svc.category) && diverse.length < 6) {
            diverse.push(svc);
            seen.add(svc.category);
          }
        }
        // Fill remaining if needed
        for (const svc of res.data) {
          if (diverse.length >= 6) break;
          if (!diverse.find(d => d.name === svc.name)) {
            diverse.push(svc);
          }
        }
        setPopularCats(diverse.slice(0, 6));
      } catch (e) {
        console.error('Error fetching popular:', e);
      }
    };
    fetchPopular();
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }
    const timeout = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/services?q=${encodeURIComponent(searchQuery)}`);
        setSearchResults(res.data);
      } catch (e) {
        console.error('Search error:', e);
      }
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchQuery]);

  return (
    <section className="pt-16 bg-white">
      <div className="max-w-[1200px] mx-auto px-4 sm:px-6">
        <div className="pt-12 pb-8 md:pt-20 md:pb-14">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[52px] font-extrabold text-gray-900 leading-tight mb-4 max-w-2xl">
            Find the best <br className="hidden sm:block" />
            <span className="text-[#37b34a]">professionals</span> in SA
          </h1>
          <p className="text-gray-500 text-base md:text-lg mb-8 font-normal">
            Get free quotes within minutes
          </p>

          <div className="relative max-w-xl">
            <div className="flex items-center bg-white border-2 border-gray-200 rounded-full overflow-hidden hover:border-gray-300 focus-within:border-[#37b34a] transition-colors duration-200 shadow-sm">
              <div className="flex items-center pl-5 pr-2">
                <Search size={20} className="text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="What service are you looking for?"
                className="flex-1 py-4 px-2 text-base text-gray-900 placeholder-gray-400 outline-none border-none bg-transparent"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setShowSuggestions(e.target.value.length > 1);
                }}
                onFocus={() => searchQuery.length > 1 && setShowSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              />
              <button className="bg-[#37b34a] hover:bg-[#2d9a3e] text-white font-semibold px-6 md:px-8 py-4 text-base transition-colors duration-200 flex items-center gap-2">
                <Search size={18} />
                <span className="hidden sm:inline">Search</span>
              </button>
            </div>

            {showSuggestions && searchResults.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 max-h-80 overflow-y-auto">
                {searchResults.slice(0, 8).map((svc, idx) => (
                  <button
                    key={idx}
                    className="w-full text-left px-5 py-3 hover:bg-gray-50 text-gray-700 text-sm transition-colors duration-150 flex items-center gap-3"
                    onClick={() => {
                      setSearchQuery(svc.name);
                      setShowSuggestions(false);
                      navigate(`/service/${svc.slug}`);
                    }}
                  >
                    <Search size={14} className="text-gray-400 flex-shrink-0" />
                    <span className="flex-1">{svc.name}</span>
                    {svc.category && (
                      <span className="text-gray-400 text-xs ml-2 flex-shrink-0">{svc.category}</span>
                    )}
                  </button>
                ))}
                {searchResults.length > 8 && (
                  <div className="px-5 py-2 text-xs text-gray-400 border-t border-gray-50">
                    +{searchResults.length - 8} more results
                  </div>
                )}
              </div>
            )}

            {showSuggestions && searchQuery.length > 1 && searchResults.length === 0 && (
              <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50">
                <div className="px-5 py-4 text-gray-500 text-sm">
                  No services found for "{searchQuery}"
                </div>
              </div>
            )}
          </div>

          <div className="mt-4 flex items-center gap-2 flex-wrap">
            <span className="text-gray-400 text-sm">Popular:</span>
            {['Plumbers', 'Web designers', 'Personal trainers', 'Accountants'].map((tag) => (
              <button
                key={tag}
                className="text-[#37b34a] hover:text-[#2d9a3e] text-sm font-medium hover:underline transition-colors duration-200"
                onClick={() => {
                  setSearchQuery(tag);
                  setShowSuggestions(true);
                }}
              >
                {tag}
              </button>
            ))}
          </div>
        </div>

        <div className="pb-10">
          <div className="flex gap-4 overflow-x-auto pb-4 scrollbar-hide">
            {[...popularCats, ...popularCats].map((cat, idx) => (
              <a key={idx} href={`/service/${cat.slug}`} className="flex-shrink-0 group cursor-pointer no-underline">
                <div className="w-[160px] h-[120px] rounded-xl overflow-hidden relative">
                  <img src={cat.image} alt={cat.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
                  <span className="absolute bottom-3 left-3 text-white text-sm font-semibold">{cat.name}</span>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
